import React, { useEffect } from 'react';
import { useStytchMemberSession, useStytchB2BClient } from '@stytch/react/b2b';
import { StytchB2B } from '@stytch/react/b2b';
import { AuthFlowType, B2BProducts } from '@stytch/vanilla-js/b2b';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

/**
 * StytchDiscoveryAuth - Uses Stytch's pre-built Discovery UI component
 * Handles authentication before org selection
 * NOT wrapped in observer() - doesn't render observable data
 */
const StytchDiscoveryAuth: React.FC = () => {
  const { session } = useStytchMemberSession();
  const navigate = useNavigate();
  const hasRedirectedRef = React.useRef(false);

  // Log EVERYTHING on component mount
  useEffect(() => {
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('🔐 [StytchDiscoveryAuth] COMPONENT MOUNTED');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('📍 Current URL:', window.location.href);
    console.log('📍 Pathname:', window.location.pathname);
    console.log('📍 Search params:', window.location.search);
    console.log('📍 Hash:', window.location.hash);
    
    // Check for Stytch tokens in URL
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const stytchTokenType = urlParams.get('stytch_token_type');
    const publicToken = urlParams.get('public_token');
    const slug = urlParams.get('slug');
    
    console.log('🎫 URL Parameters:');
    console.log('   token:', token ? `${token.substring(0, 20)}...` : 'NONE');
    console.log('   stytch_token_type:', stytchTokenType);
    console.log('   public_token:', publicToken);
    console.log('   slug:', slug);
    
    console.log('🍪 Cookies:');
    document.cookie.split('; ').forEach(cookie => {
      const [name, value] = cookie.split('=');
      if (name.startsWith('stytch')) {
        console.log(`   ${name}:`, value ? `${value.substring(0, 30)}...` : 'NONE');
      }
    });
    
    console.log('🔒 Session state:', session ? 'EXISTS' : 'NULL');
    if (session) {
      console.log('   Session ID:', session.session_id);
      console.log('   Member ID:', session.member_id);
      console.log('   Organization ID:', session.organization_id);
    }
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  }, []);

  // If already authenticated with a full session, decode JWT to get org slug and redirect
  // BUT ONLY REDIRECT ONCE to prevent loops!
  useEffect(() => {
    const redirectToApp = () => {
      console.log('🔄 [redirectToApp] Checking if redirect needed...');
      console.log('   hasRedirectedRef.current:', hasRedirectedRef.current);
      console.log('   session:', session ? 'EXISTS' : 'NULL');
      
      // CRITICAL: Only redirect ONCE per page load
      if (hasRedirectedRef.current) {
        console.log('⏭️ [StytchDiscoveryAuth] Already redirected, skipping to prevent loop');
        return;
      }
      
      // LOOP PROTECTION: Check if we detected an infinite loop
      const loopDetected = sessionStorage.getItem('auth_loop_detected');
      if (loopDetected) {
        console.error('🔴 [StytchDiscoveryAuth] Infinite loop was detected!');
        console.error('   Staying on login page - DO NOT redirect');
        sessionStorage.removeItem('auth_loop_detected');
        sessionStorage.removeItem('auth_redirect_count');
        sessionStorage.removeItem('stytch_sync_failed');
        // Force sign out from Stytch to clear session
        // TODO: Call stytch.session.revoke() if available
        return;
      }
      
      // Check if user just signed out (flag in sessionStorage)
      const justSignedOut = sessionStorage.getItem('just_signed_out');
      if (justSignedOut) {
        console.log('⏭️ [StytchDiscoveryAuth] User just signed out, skipping auto-redirect');
        sessionStorage.removeItem('just_signed_out');
        sessionStorage.removeItem('auth_redirect_count'); // Clear redirect counter on fresh login
        return;
      }
      
      if (session) {
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('✅ [StytchDiscoveryAuth] SESSION DETECTED - Processing redirect');
        console.log('   Session object:', JSON.stringify(session, null, 2));
        
        // Decode the Stytch JWT to get org info
        const cookies = document.cookie.split('; ');
        const stytchJWT = cookies.find(c => c.startsWith('stytch_session_jwt='));
        
        console.log('🍪 Found stytch_session_jwt cookie:', stytchJWT ? 'YES' : 'NO');
        
        if (stytchJWT) {
          try {
            const jwt = stytchJWT.split('=')[1];
            const payload = JSON.parse(atob(jwt.split('.')[1]));
            const orgSlug = payload['https://stytch.com/organization']?.slug;
            const orgId = payload['https://stytch.com/organization']?.organization_id;
            
            console.log('📋 JWT Payload decoded:');
            console.log('   Org ID:', orgId);
            console.log('   Org Slug:', orgSlug);
            console.log('   Member ID:', payload.sub);
            console.log('   Full org object:', payload['https://stytch.com/organization']);
            
            if (orgSlug) {
              console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
              console.log('🚀 REDIRECTING TO APP');
              console.log('   Target:', `/${orgSlug}/mindmap`);
              console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
              hasRedirectedRef.current = true; // Mark as redirected to prevent re-triggering
              navigate(`/${orgSlug}/mindmap`);
            } else {
              console.error('❌ No org slug in JWT!');
              console.error('   Full payload:', JSON.stringify(payload, null, 2));
            }
          } catch (error) {
            console.error('❌ Failed to decode Stytch JWT:', error);
          }
        } else {
          console.error('❌ No stytch_session_jwt cookie found!');
        }
      } else {
        console.log('⏳ [StytchDiscoveryAuth] No session yet, waiting...');
      }
    };
    
    redirectToApp();
  }, [session, navigate]);

  const config = {
    products: [B2BProducts.emailMagicLinks, B2BProducts.passwords, B2BProducts.oauth],
    sessionOptions: { sessionDurationMinutes: 381600 }, // 381600 minutes = 265 days
    authFlowType: AuthFlowType.Discovery,
    // Let Stytch use redirect URLs from Dashboard - don't override here!
    oauthOptions: {
      providers: [{ type: 'google' }],
    },
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
    }}>
      <div style={{
        maxWidth: '480px',
        width: '100%',
        padding: '32px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}>
        <StytchB2B
          config={config}
          callbacks={{
            onEvent: (data: any) => {
              console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
              console.log('📊 [Stytch Event Callback] Event received:');
              console.log('   Event type:', data.type);
              console.log('   Event data:', JSON.stringify(data, null, 2));
              console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
            },
          }}
        />
      </div>
    </div>
  );
};

export default StytchDiscoveryAuth;

